/**
 * Corporate Commerce server functions.
 *
 * Thin wrappers only: imports, types and `createServerFn` declarations. All
 * runtime logic lives in `corporate.server.ts` and is imported inside each
 * handler so server-function splitting can never drop it.
 */
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { CorporateQuote, CorporateQuoteStatus, QuoteRequestInput } from "./types";
import type { CorporatePackContext, QuoteUpdateInput } from "./corporate.server";

type QuoteResult = { quote: CorporateQuote | null; error: string | null };

/* ------------------------------------------------------------- storefront */

export const listCorporatePacksFn = createServerFn({ method: "GET" }).handler(async () => {
  const s = await import("./corporate.server");
  try {
    return { packs: await s.listCorporatePacks(), error: null as string | null };
  } catch (err) {
    return { packs: [], error: err instanceof Error ? err.message : "Failed to load corporate packs" };
  }
});

export const getCorporatePackFn = createServerFn({ method: "GET" })
  .inputValidator((input: { slug: string }) => {
    if (!input?.slug) throw new Error("slug required");
    return input;
  })
  .handler(async ({ data }): Promise<{ context: CorporatePackContext | null; error: string | null }> => {
    const s = await import("./corporate.server");
    try {
      return { context: await s.getCorporatePack(data.slug), error: null };
    } catch (err) {
      return { context: null, error: err instanceof Error ? err.message : "Failed to load pack" };
    }
  });

/**
 * Public quote request. Guests are allowed; when a bearer token is present the
 * owner is derived from the validated token, never from the request body.
 */
export const requestQuoteFn = createServerFn({ method: "POST" })
  .inputValidator((input: QuoteRequestInput) => {
    if (!input?.packSlug) throw new Error("A pack is required.");
    if (!input.companyName?.trim()) throw new Error("Company name is required.");
    if (!input.contactName?.trim()) throw new Error("Contact name is required.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.contactEmail ?? "")) {
      throw new Error("A valid contact email is required.");
    }
    if (!Number.isFinite(input.qty) || input.qty <= 0) throw new Error("Quantity is required.");
    return input;
  })
  .handler(async ({ data }): Promise<QuoteResult> => {
    const s = await import("./corporate.server");
    const { optionalUser } = await import("./auth.server");
    const { notifyQuote } = await import("./quote-notify.server");
    const { supabaseAdmin } = await import("@/lib/tenant/admin.server");
    try {
      const who = await optionalUser();
      const quote = await s.createQuoteRequest(
        supabaseAdmin as never,
        who.userId,
        who.email ?? data.contactEmail,
        data,
      );
      await notifyQuote("quote.requested", quote);
      return { quote, error: null };
    } catch (err) {
      return { quote: null, error: err instanceof Error ? err.message : "Could not submit your request" };
    }
  });

/* -------------------------------------------------------- customer portal */

export const myQuotesFn = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const s = await import("./corporate.server");
    return { quotes: await s.listMyQuotes(context.supabase as never, context.userId) };
  });

export const decideQuoteFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { quoteId: string; decision: "approved" | "rejected"; reason?: string }) => {
    if (!input?.quoteId) throw new Error("quoteId required");
    if (input.decision !== "approved" && input.decision !== "rejected") throw new Error("Invalid decision");
    return input;
  })
  .handler(async ({ data, context }): Promise<QuoteResult> => {
    const s = await import("./corporate.server");
    const { notifyQuote } = await import("./quote-notify.server");
    const email = (context.claims as { email?: string } | undefined)?.email ?? null;
    try {
      const quote = await s.decideQuote(
        context.supabase as never,
        context.userId,
        data.quoteId,
        data.decision,
        email,
      );
      await notifyQuote(data.decision === "approved" ? "quote.approved" : "quote.rejected", quote, {
        reason: data.reason ?? null,
      });
      return { quote, error: null };
    } catch (err) {
      return { quote: null, error: err instanceof Error ? err.message : "Could not update this quote" };
    }
  });

/* ------------------------------------------------------------------ admin */

export const adminListQuotesFn = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { search?: string; status?: CorporateQuoteStatus | "all"; salesRepEmail?: string } | undefined) => input ?? {})
  .handler(async ({ data, context }) => {
    const s = await import("./corporate.server");
    const { assertPackStaff } = await import("./staff.server");
    await assertPackStaff(context as never);
    return { quotes: await s.listQuotes(context.supabase as never, data) };
  });

export const adminGetQuoteFn = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string }) => {
    if (!input?.id) throw new Error("id required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const s = await import("./corporate.server");
    const { assertPackStaff } = await import("./staff.server");
    await assertPackStaff(context as never);
    return { quote: await s.getQuote(context.supabase as never, data.id) };
  });

export const adminUpdateQuoteFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: QuoteUpdateInput) => {
    if (!input?.id) throw new Error("id required");
    return input;
  })
  .handler(async ({ data, context }): Promise<QuoteResult> => {
    const s = await import("./corporate.server");
    const { assertPackStaff } = await import("./staff.server");
    const { notifyQuote } = await import("./quote-notify.server");
    await assertPackStaff(context as never);
    const email = (context.claims as { email?: string } | undefined)?.email ?? null;
    try {
      const quote = await s.updateQuote(context.supabase as never, data, email);
      if (data.status === "quoted") await notifyQuote("quote.ready", quote);
      if (data.status === "rejected") await notifyQuote("quote.rejected", quote);
      return { quote, error: null };
    } catch (err) {
      return { quote: null, error: err instanceof Error ? err.message : "Could not save this quote" };
    }
  });

export const adminConvertQuoteFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { quoteId: string }) => {
    if (!input?.quoteId) throw new Error("quoteId required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const s = await import("./corporate.server");
    const { assertPackStaff } = await import("./staff.server");
    const { notifyQuote } = await import("./quote-notify.server");
    await assertPackStaff(context as never);
    const email = (context.claims as { email?: string } | undefined)?.email ?? null;
    try {
      const result = await s.convertQuoteToOrder(context.supabase as never, data.quoteId, email);
      const quote = await s.getQuote(context.supabase as never, data.quoteId);
      if (quote) await notifyQuote("corporate.order.placed", quote, { orderNumber: result.orderNumber });
      return { ...result, error: null as string | null };
    } catch (err) {
      return {
        orderId: "",
        orderNumber: "",
        error: err instanceof Error ? err.message : "Could not convert this quote",
      };
    }
  });

export const corporateDashboardFn = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const s = await import("./corporate.server");
    const { assertPackStaff } = await import("./staff.server");
    await assertPackStaff(context as never);
    return { dashboard: await s.corporateDashboard(context.supabase as never) };
  });

export const orderShippingSplitsFn = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { orderId: string }) => {
    if (!input?.orderId) throw new Error("orderId required");
    return input;
  })
  .handler(async ({ data, context }) => {
    const s = await import("./corporate.server");
    return { splits: await s.listOrderShippingSplits(context.supabase as never, data.orderId) };
  });